import { FC, useState } from 'react';
import ConfirmModal from '@/components/seop/ConfirmModal';
import { useToast } from '@/contexts/ToastContext';
import { seopService } from '@/services/seop.service';
import type { SeopLotParam, SeopProductDetail } from '@/types/seop';
import { erroDeCampo, primeiraMensagem } from '@/utils/seopErrors';
import { fmtHours, fmtMoneyBRL, fmtUnits, parseHours, parseUnits } from '@/utils/seopFormat';
import { type CampoDoRascunho, mudancasDoLote, type RascunhoLote, rascunhoDe } from '@/utils/seopLotes';
import { opcoesDeRecurso } from '@/utils/seopRecursos';

const INPUT = 'w-full rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm';
const CELL = 'py-2 pr-3 text-sm text-content-primary';

interface LinhaDeLoteProps {
  lote: SeopLotParam;
  rascunho: RascunhoLote | null;
  nomes: string[];
  salvando: boolean;
  onCampo: (campo: CampoDoRascunho, valor: string) => void;
  onEditar: () => void;
  onExcluir: () => void;
  onSalvar: () => void;
  onCancelar: () => void;
}

/** Uma linha da tabela: lida, ou em edição. Produto e valor unitário não mudam aqui. */
const LinhaDeLote: FC<LinhaDeLoteProps> = ({
  lote,
  rascunho,
  nomes,
  salvando,
  onCampo,
  onEditar,
  onExcluir,
  onSalvar,
  onCancelar,
}) => {
  const identidade = `${lote.product_code} em ${lote.resource}`;
  if (!rascunho) {
    return (
      <tr className="border-b border-theme-border/50">
        <td className={CELL}>{lote.product_code}</td>
        <td className={CELL}>{lote.resource}</td>
        <td className={`${CELL} text-right tabular-nums`}>{fmtUnits(lote.lot_size)}</td>
        <td className={`${CELL} text-right tabular-nums`} title={`${lote.time_hours} h em decimal`}>
          {fmtHours(lote.time_hours)}
        </td>
        <td className={`${CELL} text-right tabular-nums`}>{fmtMoneyBRL(lote.unit_value ?? 0)}</td>
        <td className={`${CELL} whitespace-nowrap text-right`}>
          <button
            type="button"
            data-testid={`seop-editar-lote-${lote.id}`}
            aria-label={`Editar o lote de ${identidade}`}
            onClick={onEditar}
            className="rounded-lg p-1 text-content-muted hover:text-content-primary"
          >
            <span className="material-symbols-outlined text-base">edit</span>
          </button>
          <button
            type="button"
            data-testid={`seop-excluir-lote-${lote.id}`}
            aria-label={`Excluir o lote de ${identidade}`}
            onClick={onExcluir}
            className="rounded-lg p-1 text-content-muted hover:text-red-400"
          >
            <span className="material-symbols-outlined text-base">delete</span>
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-theme-border/50 bg-surface-secondary/40">
      <td className={CELL}>{lote.product_code}</td>
      <td className={CELL}>
        <select
          aria-label="Recurso do lote em edição"
          value={rascunho.resource}
          onChange={(e) => onCampo('resource', e.target.value)}
          className={INPUT}
        >
          {opcoesDeRecurso(nomes, rascunho.resource).map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
        </select>
      </td>
      <td className={CELL}>
        <input
          aria-label="Tamanho do lote em edição"
          value={rascunho.lot_size}
          onChange={(e) => onCampo('lot_size', e.target.value)}
          className={`${INPUT} text-right`}
        />
      </td>
      <td className={CELL}>
        <input
          aria-label="Tempo do lote em edição"
          value={rascunho.time_hours}
          onChange={(e) => onCampo('time_hours', e.target.value)}
          className={`${INPUT} text-right`}
        />
      </td>
      <td className={`${CELL} text-right tabular-nums`}>{fmtMoneyBRL(lote.unit_value ?? 0)}</td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        <button
          type="button"
          data-testid="seop-salvar-lote"
          onClick={onSalvar}
          disabled={salvando}
          className="rounded-lg bg-accent px-3 py-1 text-sm font-medium text-accent-contrast disabled:opacity-50"
        >
          Salvar
        </button>
        <button
          type="button"
          data-testid="seop-cancelar-lote"
          onClick={onCancelar}
          className="ml-2 rounded-lg border border-theme-border px-3 py-1 text-sm text-content-secondary"
        >
          Cancelar
        </button>
      </td>
    </tr>
  );
};

interface EditorDeLotesProps {
  produtos: SeopProductDetail[];
  nomes: string[];
  lotes: SeopLotParam[];
  onRecarregar: () => Promise<void>;
}

/**
 * Parâmetros de lote: a conversão unidades → horas de cada par (produto, recurso).
 *
 * Fora da página porque é o bloco com CRUD completo — sem editar e sem excluir aqui, o
 * conserto de um cadastro errado passava por apagar o produto inteiro, e com ele a
 * demanda e o roteiro (report do Herrison).
 */
const EditorDeLotes: FC<EditorDeLotesProps> = ({ produtos, nomes, lotes, onRecarregar }) => {
  const { showToast } = useToast();
  const [novo, setNovo] = useState({ product: '', resource: '', lot_size: '', time_hours: '' });
  const [erroNovo, setErroNovo] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<RascunhoLote | null>(null);
  const [erroEdicao, setErroEdicao] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<SeopLotParam | null>(null);
  const [salvando, setSalvando] = useState(false);

  const adicionar = async () => {
    setErroNovo(null);
    const tamanho = parseUnits(novo.lot_size);
    const tempo = parseHours(novo.time_hours);
    if (!novo.product || !novo.resource) {
      setErroNovo('Escolha o produto e o recurso.');
      return;
    }
    // Ver o comentário de `mudancasDoLote`: zero aqui vira etapa silenciosamente
    // ignorada no cálculo, não erro visível.
    if (!(tamanho > 0)) {
      setErroNovo('O tamanho do lote precisa ser maior que zero.');
      return;
    }
    if (!(tempo > 0)) {
      setErroNovo('O tempo em horas precisa ser maior que zero.');
      return;
    }
    setSalvando(true);
    try {
      await seopService.createLotParam({
        product: novo.product,
        resource: novo.resource,
        lot_size: tamanho,
        time_hours: tempo,
      });
      setNovo({ product: '', resource: '', lot_size: '', time_hours: '' });
      showToast('Parâmetro de lote criado.', 'success');
      await onRecarregar();
    } catch (err) {
      const mensagem = erroDeCampo(err, 'lot_size') ?? erroDeCampo(err, 'resource') ?? primeiraMensagem(err);
      setErroNovo(mensagem);
      if (!mensagem) showToast('Erro ao criar o parâmetro de lote.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const salvarEdicao = async () => {
    const original = lotes.find((l) => l.id === rascunho?.id);
    if (!rascunho || !original) return;
    setErroEdicao(null);
    const { payload, erro } = mudancasDoLote(original, rascunho);
    if (erro) {
      setErroEdicao(erro);
      return;
    }
    if (Object.keys(payload).length === 0) {
      setRascunho(null);
      return;
    }
    setSalvando(true);
    try {
      await seopService.updateLotParam(original.id, payload);
      setRascunho(null);
      showToast('Parâmetro de lote atualizado.', 'success');
      await onRecarregar();
    } catch (err) {
      setErroEdicao(primeiraMensagem(err) ?? 'Erro ao salvar o parâmetro de lote.');
    } finally {
      setSalvando(false);
    }
  };

  const confirmarExclusao = async () => {
    if (!excluindo) return;
    setSalvando(true);
    try {
      await seopService.deleteLotParam(excluindo.id);
      setExcluindo(null);
      showToast('Parâmetro de lote excluído.', 'success');
      await onRecarregar();
    } catch (err) {
      showToast(primeiraMensagem(err) ?? 'Erro ao excluir o parâmetro de lote.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const abrirEdicao = (lote: SeopLotParam) => {
    setErroEdicao(null);
    setRascunho(rascunhoDe(lote));
  };

  return (
    <>
      <div className="mb-3 grid gap-3 md:grid-cols-4">
        <label className="text-xs text-content-muted">
          Produto
          <select
            aria-label="Produto do lote"
            value={novo.product}
            onChange={(e) => setNovo({ ...novo, product: e.target.value })}
            className={`${INPUT} mt-1`}
          >
            <option value="">—</option>
            {produtos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-content-muted">
          Recurso
          <select
            aria-label="Recurso do lote"
            value={novo.resource}
            onChange={(e) => setNovo({ ...novo, resource: e.target.value })}
            className={`${INPUT} mt-1`}
          >
            <option value="">—</option>
            {nomes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-content-muted">
          Tamanho do lote
          <input
            aria-label="Tamanho do lote"
            value={novo.lot_size}
            onChange={(e) => setNovo({ ...novo, lot_size: e.target.value })}
            className={`${INPUT} mt-1`}
          />
        </label>
        <label className="text-xs text-content-muted">
          Tempo por lote (h:mm)
          <input
            aria-label="Tempo do lote em horas"
            placeholder="2:40"
            value={novo.time_hours}
            onChange={(e) => setNovo({ ...novo, time_hours: e.target.value })}
            className={`${INPUT} mt-1`}
          />
        </label>
      </div>
      {erroNovo && (
        <p data-testid="seop-erro-lote" className="mb-3 text-sm text-theme-danger">
          {erroNovo}
        </p>
      )}
      <button
        type="button"
        data-testid="seop-adicionar-lote"
        onClick={adicionar}
        disabled={salvando}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
      >
        Adicionar parâmetro
      </button>
      <p className="mt-2 text-xs text-content-muted">
        O tempo aceita <strong>2:40</strong> ou <strong>2,67</strong> — a tabela mostra sempre em h:mm.
      </p>

      {erroEdicao && (
        <p data-testid="seop-erro-edicao-lote" className="mt-3 text-sm text-theme-danger">
          {erroEdicao}
        </p>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="pb-2 pr-3 text-left">Produto</th>
              <th className="pb-2 pr-3 text-left">Recurso</th>
              <th className="pb-2 pr-3 text-right">Lote</th>
              <th className="pb-2 pr-3 text-right">Horas (h:mm)</th>
              <th className="pb-2 pr-3 text-right">Valor unit.</th>
              <th className="pb-2 text-right">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lotes.map((l) => (
              <LinhaDeLote
                key={l.id}
                lote={l}
                rascunho={rascunho?.id === l.id ? rascunho : null}
                nomes={nomes}
                salvando={salvando}
                onCampo={(campo, valor) => setRascunho((r) => (r ? { ...r, [campo]: valor } : r))}
                onEditar={() => abrirEdicao(l)}
                onExcluir={() => setExcluindo(l)}
                onSalvar={salvarEdicao}
                onCancelar={() => {
                  setRascunho(null);
                  setErroEdicao(null);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={excluindo !== null}
        title={`Excluir o lote de ${excluindo?.product_code ?? ''} em ${excluindo?.resource ?? ''}?`}
        confirmLabel="Excluir"
        isBusy={salvando}
        onConfirm={confirmarExclusao}
        onCancel={() => setExcluindo(null)}
      >
        <p data-testid="seop-impacto-lote">
          Sem este parâmetro o produto <strong>{excluindo?.product_code}</strong> deixa de consumir horas
          em <strong>{excluindo?.resource}</strong> — a etapa some do cálculo, sem aviso.
        </p>
        <p>Esta ação não tem desfazer — salve um cenário antes se quiser poder voltar.</p>
      </ConfirmModal>
    </>
  );
};

export default EditorDeLotes;
